import Wkt from "wicket";
import wktToFeatures from "common/wktToFeatures";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { 
  ApplicationDetailState, 
  Inspection, 
  Defect, 
  NotifyWorkStartResponse, 
  NotifyWorkCompletionResponse, 
  MaintenanceCompletionResponse,
  Participant,
  ReplaceParticipantResponse,
  AuditHistoryResponse,
  LocationInformationModel,
  Attachment
} from "./types";
import { WorkInformationModel } from "containers/ApplicationDetail/EditWorkInformationModal";

(pdfMake as any).vfs = (pdfFonts as any).pdfMake ? (pdfFonts as any).pdfMake.vfs : pdfFonts;

export const processApplicationShapes = (draft: ApplicationDetailState, shapes: string) => {
  if (shapes) {
    const wicket = new Wkt.Wkt();
    const features = wktToFeatures(wicket, shapes);
    draft.applicationDetailData!.features = features;
  }
};

export const processRcaBoundaries = (draft: ApplicationDetailState, rcaBoundaries: string) => {
  if (rcaBoundaries) {
    const wicket = new Wkt.Wkt();
    const features = wktToFeatures(wicket, rcaBoundaries);
    draft.applicationDetailData!.rcaBoundaryFeatures = features;
  }
};

export const processDateFields = (draft: ApplicationDetailState) => {
  if (!draft.applicationDetailData) return;

  const app = draft.applicationDetailData;
  
  app.dateSubmitted = app.dateSubmitted ? app.dateSubmitted : undefined;
  app.dateTmpLodged = app.dateTmpLodged ? app.dateTmpLodged : undefined;
  app.dateApproved = app.dateApproved ? app.dateApproved : undefined;
  app.dateWorkSignedOff = app.dateWorkSignedOff ? app.dateWorkSignedOff : undefined;
  app.dateWarrantyStarted = app.dateWarrantyStarted ? app.dateWarrantyStarted : undefined;
  app.dateWarrantyCompletionNotified = app.dateWarrantyCompletionNotified ? app.dateWarrantyCompletionNotified : undefined;
  app.dateWarrantyEnded = app.dateWarrantyEnded ? app.dateWarrantyEnded : undefined;
  app.dateClosed = app.dateClosed ? app.dateClosed : undefined;
  app.dateWorkStarted = app.dateWorkStarted ? app.dateWorkStarted : undefined;
  app.dateWorkCompleted = app.dateWorkCompleted ? app.dateWorkCompleted : undefined;
};

export const processInspectionsAndDefects = (draft: ApplicationDetailState) => {
  if (draft.applicationDetailData?.inspectionsAndDefects) {
    draft.applicationDetailData.inspectionsAndDefects = draft.applicationDetailData.inspectionsAndDefects.map(
      x => ({ ...x, addedOn: new Date(x.addedOn) })
    );
  }
};

export const processApplicationFees = (draft: ApplicationDetailState) => {
  if (draft.applicationDetailData?.applicationFees) {
    draft.applicationDetailData.applicationFees = draft.applicationDetailData.applicationFees.map(
      x => ({ ...x, dateAdded: x.dateAdded })
    );
  }
};

export const processParticipants = (draft: ApplicationDetailState) => {
  if (draft.applicationDetailData?.participants) {
    draft.applicationDetailData.participants = draft.applicationDetailData.participants.map(
      x => x.certificateDetails
        ? {
            ...x,
            certificateDetails: {
              ...x.certificateDetails,
              certificateExpiry: x.certificateDetails.certificateExpiry
                ? new Date(x.certificateDetails.certificateExpiry)
                : undefined
            }
          }
        : x
    );
  }
};

export const generateInspectionPdf = (inspection: Inspection) => {
  const newDate = new Date();
  const month = newDate.toLocaleString("default", { month: "long" });
  const dateString = `${newDate.getDate()} ${month} ${newDate.getFullYear()} ${newDate.getHours()}-${newDate.getMinutes()}-${newDate.getSeconds()}`;

  const docDefinition: any = {
    content: [
      { text: "INSPECTION", style: "header", alignment: "center" },
      {
        table: {
          widths: [150, "*"],
          body: [
            ["CAR Number", inspection.carNumber],
            ["Inspection Id", inspection.inspectionId],
            [
              "Date",
              inspection.date
                ? new Date(inspection.date).toLocaleDateString("en-GB")
                : ""
            ],
            ["Auditor", inspection.auditor],
            ["Type", inspection.type],
            ["Category", inspection.category],
            ["Rating", inspection.rating],
            ["Notes", inspection.notes],
            ["Remedial work required", inspection.remedialWorkRequired]
          ]
        },
        layout: "lightHorizontalLines"
      }
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10]
      }
    }
  };

  pdfMake.createPdf(docDefinition).download(`Inspection Report generated on ${dateString}.pdf`);
};

export const generateDefectPdf = (defect: Defect) => {
  const newDate = new Date();
  const month = newDate.toLocaleString("default", { month: "long" });
  const dateString = `${newDate.getDate()} ${month} ${newDate.getFullYear()} ${newDate.getHours()}-${newDate.getMinutes()}-${newDate.getSeconds()}`;

  const docDefinition: any = {
    content: [
      { text: "DEFECT", style: "header", alignment: "center" },
      {
        table: {
          widths: [150, "*"],
          body: [
            ["CAR Number", defect.carNumber],
            ["Defect Id", defect.defectId],
            [
              "Date Found",
              defect.dateFound
                ? new Date(defect.dateFound).toLocaleDateString("en-GB")
                : ""
            ],
            ["Source", defect.source],
            ["Status", defect.status],
            ["Description", defect.description],
            ["Explanation", defect.explanation],
            [
              "Date Fixed",
              defect.dateFixed
                ? new Date(defect.dateFixed).toLocaleDateString("en-GB")
                : ""
            ],
            ["Resolution", defect.resolutionNote],
            ["Rating", defect.rating]
          ]
        },
        layout: "lightHorizontalLines"
      }
    ],
    styles: {
      header: {
        fontSize: 18,
        bold: true,
        margin: [0, 0, 0, 10]
      }
    }
  };

  pdfMake.createPdf(docDefinition).download(`Defect Report generated on ${dateString}.pdf`);
};

export const processCertificateExpiry = (participant: Participant) => {
  if (participant.certificateDetails?.certificateExpiry) {
    participant.certificateDetails.certificateExpiry = new Date(
      participant.certificateDetails.certificateExpiry
    );
  }
};

export const handleWorkInformationUpdate = (draft: ApplicationDetailState, workInfo: WorkInformationModel) => {
  if (!draft.applicationDetailData) return;
  
  draft.applicationDetailData.applicantRole = workInfo.applicantRole;
  draft.applicationDetailData.activities = workInfo.activities;
  draft.applicationDetailData.majorActivities = workInfo.majorActivities;
  draft.applicationDetailData.roadLevel = workInfo.roadLevel;
  draft.applicationDetailData.globalCar = workInfo.isGlobalCar;
  draft.applicationDetailData.isRoadClosureRequired = workInfo.isRoadClosureRequired;
  draft.applicationDetailData.estimatedDuration = Number(workInfo.estimatedDuration);
  draft.applicationDetailData.estimatedStartDate = workInfo.estimatedStartDate!;
  draft.applicationDetailData.estimatedCompletionDate = workInfo.estimatedCompletionDate!;
  draft.applicationDetailData.description = workInfo.description;
  draft.applicationDetailData.hoursOfWork = workInfo.hoursOfWork;
  draft.applicationDetailData.utilityRefNumber = workInfo.utilityRefNumber;
  draft.applicationDetailData.consentNumber = workInfo.consentNumber;
  draft.applicationDetailData.purchaseOrderNumber = workInfo.purchaseOrderNumber;
};

export const handleNotifyWorkStart = (draft: ApplicationDetailState, updatedInfo: NotifyWorkStartResponse) => {
  if (!draft.applicationDetailData) return;
  
  draft.applicationDetailData.dateWorkStarted = updatedInfo.dateWorkStarted
    ? updatedInfo.dateWorkStarted.toISOString()
    : undefined;
  draft.applicationDetailData.dateWorkCompleted = updatedInfo.dateWorkCompleted
    ? updatedInfo.dateWorkCompleted.toISOString()
    : undefined;
  draft.applicationDetailData.estimatedDuration = updatedInfo.duration;
  draft.applicationDetailData.carStatus = updatedInfo.carStatus;
  draft.applicationDetailData.dateWorkStartedStatus = updatedInfo.dateWorkStartedStatus;
};

export const handleLoadAuditLogs = (draft: ApplicationDetailState, auditLog: AuditHistoryResponse | null) => {
  if (!draft.applicationDetailData) return;
  
  if (!auditLog) {
    draft.applicationDetailData.dateAuditLog = null;
  } else {
    draft.applicationDetailData.dateAuditLog = {
      ...auditLog,
      addedOn: new Date(auditLog.addedOn)
    };
  }
};

export const handleNotifyWorkCompletion = (draft: ApplicationDetailState, updatedInfo: NotifyWorkCompletionResponse) => {
  if (!draft.applicationDetailData) return;
  
  draft.applicationDetailData.dateWorkCompleted = updatedInfo.dateWorkCompleted
    ? updatedInfo.dateWorkCompleted.toISOString()
    : undefined;
  draft.applicationDetailData.dateWorkCompletedStatus = updatedInfo.dateWorkCompletedStatus;
  draft.applicationDetailData.notes = updatedInfo.notes;
  draft.applicationDetailData.carStep = updatedInfo.carStep;
  draft.applicationDetailData.carStatus = updatedInfo.carStatus;
};

export const handleLocationUpdate = (draft: ApplicationDetailState, locationInfo: LocationInformationModel) => {
  if (!draft.applicationDetailData) return;
  
  draft.applicationDetailData = {
    ...draft.applicationDetailData,
    features: locationInfo.features,
    streetName: locationInfo.streetName,
    streetNumber: locationInfo.streetNumber,
    suburb: locationInfo.suburb,
    townCity: locationInfo.townCity,
    postcode: locationInfo.postCode,
    addressNotes: locationInfo.addressNotes,
    locationsInRoad: locationInfo.locationsInRoad
  };
};

export const handleMaintenanceCompletion = (draft: ApplicationDetailState, model: MaintenanceCompletionResponse) => {
  if (!draft.applicationDetailData) return;
  
  draft.applicationDetailData.dateWarrantyCompletionNotified = model.dateWarrantyCompletionNotified
    ? model.dateWarrantyCompletionNotified.toISOString()
    : undefined;
  draft.applicationDetailData.carStatus = model.carStatus;
  draft.applicationDetailData.notes = model.notes;
};

export const handleReplaceParticipant = (draft: ApplicationDetailState, participantResponse: ReplaceParticipantResponse) => {
  processCertificateExpiry(participantResponse.participant);
  draft.applicationDetailData!.participants = draft.applicationDetailData!.participants!.map(
    x => {
      if (x.participantLinkId === participantResponse.participant.participantLinkId) {
        return participantResponse.participant;
      }
      return x;
    }
  );
  draft.applicationDetailData!.permissionValue = participantResponse.permissionValue;
};

export const handleUploadDetailFile = (draft: ApplicationDetailState, attachments: Attachment[]) => {
  if (draft.applicationDetailData) {
    draft.applicationDetailData.attachments = attachments.concat(
      draft.applicationDetailData.attachments ?? []
    );
  }
};

export const handleDeleteAttachment = (draft: ApplicationDetailState, attachmentReference: string) => {
  if (draft.applicationDetailData) {
    draft.applicationDetailData.attachments = draft.applicationDetailData.attachments?.filter(
      x => x.attachmentReference !== attachmentReference
    );
  }
};
